import { useState, useEffect } from "react";
import ExcercisePlanLoader from "@components/ui/Loaders/ExcercisePlanLoader";
import Robot from "@assets/images/Robot.svg";
import QuizGameStepper from "./QuizGameStepper";
import { ReactComponent as Bulb } from "@assets/images/bulb.svg";
import ChallengeFinished from "./ChallengeFinshed";
import BackImg from "@assets/images/stepsBack.jpg";
import { useLocation, useNavigate } from "react-router-dom";
import { submitAnswers, getQuizData, getQuizById } from "api/quizGame"; // Add getQuizData import
import { NavigationPrompt } from "@utils/NavigationBlocker";
import { toast } from "react-toastify";
import ButtonLoader from "@components/ui/Loaders/ButtonLoader";
import { useTranslation } from "react-i18next";
import ActualDietPlanLoader from "@components/ui/Loaders/ActualDietPlanLoader";
import { ReturnDashboard } from "@utils/defaultImages";
import { useUI } from "context/LoadingContext";

const QuizQuestionaire = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Initial state
  const [quizId, setQuizId] = useState(null);
  const [quizData, setQuizData] = useState([]);
  const [quizTitle, setQuizTitle] = useState("");
  const [totalPoints, setTotalPoints] = useState(0);
  const [isLoadingQuiz, setIsLoadingQuiz] = useState(true);

  // Existing state
  const [currentStep, setCurrentStep] = useState(0);
  const [pointsEarned, setPointsEarned] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [submittedAnswers, setSubmittedAnswers] = useState({});
  const [selectedOption, setSelectedOption] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [timerActive, setTimerActive] = useState(false);
  const [showFinalResult, setShowFinalResult] = useState(false);
  const [badgeAchived, setBadgeAchived] = useState(null);
  const [placesAchived, setPlacesAchived] = useState(null);

  const { setIsActionLocked } = useUI();

  // Initialize quiz data
  useEffect(() => {
    const initializeQuiz = async () => {
      // Check if we have quiz_id from state
      const quizIdFromState = location?.state?.quiz_id;

      if (!quizIdFromState) {
        // No quiz_id, redirect to start-quiz
        navigate("/quiz-game/start-quiz");
        return;
      }

      setQuizId(quizIdFromState);

      try {
        setIsLoadingQuiz(true);

        // Fetch quiz data from API
        const response = await getQuizById(quizIdFromState);

        // Set quiz data from API response
        setQuizData(response?.questions || []);
        setQuizTitle(response?.quizTitle || "Daily Wellness Quiz");
        setTotalPoints(response?.totalPoints || 0);
      } catch (error) {
        console.error("Error fetching quiz data:", error);
        toast.error(t("toast.Failed to load quiz data. Please try again"));
        setIsActionLocked(false);
        navigate("/quiz-game/start-quiz");
      } finally {
        setIsLoadingQuiz(false);
      }
    };

    initializeQuiz();
  }, []);

  // Get current question details
  const currentQuestion = quizData?.[currentStep];
  const currentTimeLimit = currentQuestion?.timeLimit || 0;
  const isSubmitted = submittedAnswers[currentStep];
  const currentAnswer = submittedAnswers[currentStep];

  // Timer effect
  useEffect(() => {
    // Reset timer when question changes
    if (currentTimeLimit > 0) {
      setTimeLeft(currentTimeLimit);
      setTimerActive(true);
    } else {
      setTimerActive(false);
    }

    // Cleanup function to clear timer
    return () => {
      setTimerActive(false);
    };
  }, [currentStep, currentTimeLimit]);

  useEffect(() => {
    setIsActionLocked(true);
    return () => {
      setIsActionLocked(false);
    };
  }, []);

  // Timer countdown effect
  useEffect(() => {
    if (!timerActive || timeLeft <= 0 || isSubmitted) return;

    const timerId = setTimeout(() => {
      setTimeLeft(timeLeft - 1);
    }, 1000);

    return () => clearTimeout(timerId);
  }, [timeLeft, timerActive, isSubmitted]);

  // Handle time expiration when timer reaches 0
  useEffect(() => {
    if (timeLeft === 0 && timerActive && !isSubmitted) {
      handleTimeExpired();
    }
  }, [timeLeft, timerActive, isSubmitted]);

  // Handle time expiration
  const handleTimeExpired = () => {
    setTimerActive(false);

    const currentQuestion = quizData[currentStep];

    if (selectedOption) {
      // User picked an option but didn't submit → auto-submit
      const selectedOptionData = currentQuestion.options.find(
        (opt) => opt.id === selectedOption
      );
      const isCorrect = selectedOptionData.isCorrect;

      setSubmittedAnswers({
        ...submittedAnswers,
        [currentStep]: {
          selectedOption,
          isCorrect,
          feedback: selectedOptionData.feedback,
          didYouKnow: selectedOptionData.didYouKnow,
          points: isCorrect ? currentQuestion.points : 0,
          timeExpired: true,
        },
      });

      if (isCorrect) {
        setPointsEarned(pointsEarned + currentQuestion.points);
      }
    } else {
      // User didn't select anything → show correct answer
      const correctOption = currentQuestion.options.find(
        (opt) => opt.isCorrect
      );

      setSubmittedAnswers({
        ...submittedAnswers,
        [currentStep]: {
          selectedOption: null,
          isCorrect: false,
          feedback: t("quizGame.Time's up! You didn't answer in time"),
          didYouKnow: correctOption.didYouKnow,
          points: 0,
          timeExpired: true,
          showCorrectAnswer: true,
          correctOptionId: correctOption.id,
        },
      });
    }
  };

  // Handle option selection
  const handleOptionSelect = (optionId) => {
    if (!isSubmitted) {
      setSelectedOption(optionId);
    }
  };

  // Handle answer submission
  const handleSubmit = () => {
    if (selectedOption === null) return;

    const currentQuestion = quizData[currentStep];
    const selectedOptionData = currentQuestion.options.find(
      (opt) => opt.id === selectedOption
    );
    const isCorrect = selectedOptionData.isCorrect;

    // Stop timer if active
    setTimerActive(false);

    // Mark answer as submitted
    setSubmittedAnswers({
      ...submittedAnswers,
      [currentStep]: {
        selectedOption: selectedOption,
        isCorrect: isCorrect,
        feedback: selectedOptionData.feedback,
        didYouKnow: selectedOptionData.didYouKnow,
        points: isCorrect ? currentQuestion.points : 0,
      },
    });

    // Update points if correct
    if (isCorrect) {
      setPointsEarned(pointsEarned + currentQuestion.points);
    }
  };

  // Handle moving to next question
  const handleNextQuestion = async () => {
    if (currentStep < quizData.length - 1) {
      setCurrentStep(currentStep + 1);
      setSelectedOption(null);
    } else {
      // Quiz completed logic
      const values = Object.values(submittedAnswers);
      const correctCount = values.filter((item) => item.isCorrect).length;
      const answeredCount = values.filter(
        (item) => item.selectedOption !== null
      ).length;
      const accuracy = Math.round((correctCount / quizData?.length) * 100) ?? 0
          
      const pointeEarned = values.reduce((sum, item) => sum + item.points, 0);

      const data = {
        quiz_id: quizId, // Use the quizId state
        correct_answers: correctCount,
        accuracy: accuracy,
        point_earned: pointeEarned,
      };

      try {
        setIsLoading(true);
        const response = await submitAnswers(data);
        setBadgeAchived(response?.recent_milestone_achieved);
        setPlacesAchived(response?.recent_place_achieved);

        navigate("/quiz-game/challenge-finished", {
          state: {
            submittedAnswers: submittedAnswers,
            totalPoints: totalPoints,
            questionsLength: quizData?.length,
            badgeAchived: response?.recent_milestone_achieved,
            placesAchived: response?.recent_place_achieved,
            summary: response?.summary,
          },
        });

        toast.info(t("toast.Quiz submitted successfully"));
      } catch (error) {
        console.log("error in submitting quiz");
        toast.error(t("toast.Failed to sumbit quiz. PLease try again"));
      } finally {
        setIsLoading(false);
      }
    }
  };

  const conditionalCheck =
    currentTimeLimit && timeLeft === 0 && !selectedOption;

  // Show loading while fetching quiz data
  if (isLoadingQuiz) {
    return <ActualDietPlanLoader quizGame={true} />;
  }

  // Show error state if no quiz data
  if (!quizData || quizData.length === 0) {
    setIsActionLocked(false);

    return (
      <div className="bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-6 rounded-xl">
        <div className="w-full">
          {/* Animated Illustration */}
          <div className="relative mb-8">
            <div className="w-32 h-32 mx-auto bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full flex items-center justify-center">
              <svg
                className="w-16 h-16 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                />
              </svg>
            </div>

            {/* Floating elements */}
            <div className="absolute -top-2 -right-2 w-6 h-6 bg-yellow-400 rounded-full animate-bounce"></div>
            <div className="absolute -bottom-2 -left-2 w-4 h-4 bg-green-400 rounded-full animate-pulse"></div>
          </div>

          {/* Content */}
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-800 mb-3">
              {/* {t("quizGame.quizzesNotLoadingTitle")} */}
              Unable to Load Quizzes
            </h2>

            <p className="text-lg text-gray-600 mb-2">
              {/* {t("quizGame.quizzesNotLoadingDescription")} */}
              We encountered an issue while loading your quizzes.
            </p>

            <p className="text-gray-500 mb-8">
              {/* {t("quizGame.quizzesNotLoadingSubtitle")} */}
              Don't worry, your created quizzes are safe. Please try refreshing
              or check your connection.
            </p>

            {/* Primary Action - Try Again */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button
                onClick={() => navigate("/quiz-game/start-quiz")}
                className="group relative bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-semibold py-4 px-8 rounded-2xl shadow-lg transition-all duration-300 transform hover:scale-105 hover:shadow-xl w-full max-w-xs !text-[16px] md:text-[20px]"
              >
                <span className="absolute inset-0 w-full h-full bg-white opacity-0 group-hover:opacity-10 transition-opacity duration-300 rounded-2xl"></span>

                <span className="flex items-center justify-center gap-3">
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  {/* {t("quizGame.tryAgain")} */}
                  Try Again
                </span>
              </button>

              {/* Secondary Action - Create New Quiz */}
              <button
                className="bg-[#64DDFF] py-[8px] px-6 text-[16px] lg:text-[20px] font-[600] font-[Lato] text-white rounded-lg flex items-center min-h-[60px] gap-2"
                onClick={() => navigate("/quiz-game")}
              >
                {t("quizGame.Return To Dashboard")}
                <ReturnDashboard />
              </button>
            </div>
          </div>

          {/* Decorative elements */}
          {/* <div className="mt-12 flex justify-center space-x-2">
          {[1, 2, 3].map((dot) => (
            <div
              key={dot}
              className="w-2 h-2 bg-gray-300 rounded-full animate-pulse"
              style={{ animationDelay: `${dot * 0.3}s` }}
            ></div>
          ))}
        </div> */}
        </div>
      </div>
    );
  }

  return (
    <>
      <NavigationPrompt when={!showFinalResult} />
      {showFinalResult ? (
        <ChallengeFinished
          submittedAnswers={submittedAnswers}
          questionsLength={quizData?.length}
          totalPoints={totalPoints}
          badgeAchived={badgeAchived}
          placesAchived={placesAchived}
        />
      ) : (
        <div className={`h-[100%] max-h-[100%]`}>
          <div
            id="chatgpt-chatbox"
            className="rounded-lg shadow-lg w-full bg-white rounded-t-[16px] bg-cover"
          >
            <div
              className="bg-cover px-[20px] py-[50px] xl:px-[65px] relative text-white rounded-t-[16px]"
              style={{
                backgroundImage: "url('/Fitbot_New_BG.png')",
                backgroundPosition: "bottom",
              }}
            >
              <h1 className="font-[700] font-[Lato] text-[24px]">
                {t("quizGame.Daily Wellness Quiz")}
              </h1>
              <p className="font-[400] font-[Open Sans] text-[14px]">
                {t(
                  "quizGame.Answer quick questions to earn points, learn tips, and unlock rewards"
                )}
              </p>
            </div>

            <div className="px-[20px] xl:px-[65px] pt-2 pb-9">
              <QuizGameStepper
                currentStep={currentStep}
                steps={quizData}
                quizTitle={quizTitle}
                pointsEarned={pointsEarned}
                totalPoints={totalPoints}
                timeLeft={timeLeft}
                timeLimit={currentTimeLimit}
              />

              <div className="overflow-auto my-5 bg-gray-50">
                <div className="flex mb-1 gap-2">
                  <div
                    className="bg-cover p-5 rounded-[13px] text-white text-[15px] font-[Lato] font-[400] w-full flex flex-col md:flex-row justify-between items-center gap-2"
                    style={{ backgroundImage: `url(${BackImg})` }}
                  >
                    <div className="w-full ">
                      <p className="text-white text-[20px] font-[Lato] font-[400] mb-4">
                        {currentQuestion?.questionText}
                      </p>

                      <div className="bg-white rounded-xl p-3 my-2">
                        {/* Show time expired message only when time is up and no option selected and no answer submitted yet */}
                        {currentTimeLimit &&
                          timeLeft === 0 &&
                          !selectedOption && (
                            <div className="mb-3">
                              <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-3 animate-pulse w-full">
                                <div className="bg-red-100 rounded-full p-2">
                                  <svg
                                    className="w-6 h-6 text-red-600"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth="2"
                                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                    ></path>
                                  </svg>
                                </div>
                                <div>
                                  <p className="font-medium text-red-800">
                                    {t("quizGame.Time's Up")}
                                  </p>
                                  <p className="text-sm text-red-600">
                                    {t(
                                      "quizGame.You didn't select any answer in time"
                                    )}
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}

                        {currentQuestion?.options?.map((item) => {
                          const isSelected = selectedOption === item.id;
                          const isCorrectOption = item.isCorrect;
                          const isSubmittedForThisQuestion =
                            submittedAnswers[currentStep];
                          const userSelectedOption = isSubmittedForThisQuestion
                            ? submittedAnswers[currentStep].selectedOption
                            : null;

                          // Check if we need to show the correct answer (time expired with no selection)
                          const showCorrectAnswer =
                            isSubmittedForThisQuestion &&
                            isSubmittedForThisQuestion.showCorrectAnswer;

                          // Fixed logic: Only show selection if user actually selected something
                          // When time expires and no option was selected, don't highlight any option
                          const showAsSelected = isSubmittedForThisQuestion
                            ? userSelectedOption === item.id &&
                              userSelectedOption !== null
                            : isSelected;

                          // Determine if we should show this option as correct (when time expires)
                          const showAsCorrect =
                            showCorrectAnswer && isCorrectOption;

                          return (
                            <div
                              key={item.id}
                              className={`flex items-center p-2 mb-2 rounded-lg border border-[transparent] transition-all ${
                                !conditionalCheck && isSubmitted
                                  ? isCorrectOption
                                    ? "bg-green-100 border border-green-300"
                                    : showAsSelected
                                    ? "bg-red-100 border border-red-300"
                                    : "bg-gray-50"
                                  : showAsSelected
                                  ? "bg-blue-50 border border-blue-300"
                                  : showAsCorrect
                                  ? "bg-green-100 border border-green-300" // Highlight correct answer
                                  : "bg-white hover:bg-gray-50"
                              } ${
                                isSubmitted ||
                                (currentTimeLimit &&
                                  timeLeft === 0 &&
                                  !selectedOption)
                                  ? "cursor-default"
                                  : "cursor-pointer"
                              }`}
                              onClick={() => {
                                // Disable clicking when submitted or when time expired with no selection
                                if (
                                  !isSubmitted &&
                                  !(
                                    currentTimeLimit &&
                                    timeLeft === 0 &&
                                    !selectedOption
                                  )
                                ) {
                                  handleOptionSelect(item.id);
                                }
                              }}
                            >
                              <div
                                className={`flex items-center justify-center min-w-5 min-h-5 rounded-full border border-[transparent] mr-3 ${
                                  !conditionalCheck && isSubmitted
                                    ? isCorrectOption
                                      ? "bg-green-500 border-green-500"
                                      : showAsSelected
                                      ? "bg-red-500 border-red-500"
                                      : "border-gray-300"
                                    : showAsSelected
                                    ? "bg-blue-500 border-blue-500"
                                    : showAsCorrect
                                    ? "bg-green-500 border-green-500" // Show green for correct answer
                                    : "border-gray-300"
                                }`}
                              >
                                {isSubmitted ? (
                                  isCorrectOption ? (
                                    <span className="text-white text-xs">
                                      ✓
                                    </span>
                                  ) : showAsSelected ? (
                                    <span className="text-white text-xs">
                                      ✗
                                    </span>
                                  ) : showAsCorrect ? (
                                    (<span className="text-white text-xs">✓
                                                                          </span>) // Show checkmark for correct answer
                                  ) : null
                                ) : showAsSelected ? (
                                  <div className="w-3 h-3 rounded-full bg-white"></div>
                                ) : showAsCorrect ? (
                                  (<span className="text-white text-xs">✓</span>) // Show checkmark for correct answer
                                ) : null}
                              </div>
                              <p className="text-[16px] font-[Lato] text-black">
                                {item.text}
                              </p>
                            </div>
                          );
                        })}
                      </div>

                      {!isSubmitted &&
                      !(
                        currentTimeLimit &&
                        timeLeft === 0 &&
                        !selectedOption
                      ) ? (
                        <button
                          className="bg-[#64DDFF] py-[10px] text-white rounded-full mt-2 w-full disabled:cursor-not-allowed"
                          onClick={handleSubmit}
                          disabled={selectedOption === null}
                        >
                          {t("quizGame.Submit")}
                        </button>
                      ) : (
                        <button className="bg-gray-400 py-[10px] text-white rounded-full mt-2 w-full cursor-default">
                          {isSubmitted
                            ? t("quizGame.Submitted")
                            : t("quizGame.Time Expired")}
                        </button>
                      )}
                    </div>

                    <div className="w-full ">
                      {/* Feedback section after submission or time expiration */}
                      {isSubmitted && (
                        <div className="p-5  text-white text-[15px] font-[Lato] font-[400] w-full">
                          <div className="mb-3 flex flex-col  xl:flex-row xl:items-center gap-2 justify-between">
                            <p
                              className={`font-[Lato] text-center font-[600] text-white text-[16px] border border-[#64DDFF] py-1 px-2 rounded-full`}
                            >
                              {currentAnswer.isCorrect
                                ? `✅ ${t("quizGame.Correct")}!`
                                : currentAnswer.timeExpired && !selectedOption
                                ? ""
                                : `⛔ ${t("quizGame.Incorrect")}! `}
                              {currentAnswer.timeExpired &&
                                `🕗 ${t("quizGame.Time Expired")}!`}
                            </p>
                            {currentAnswer.isCorrect && (
                              <div className="flex justify-center bg-[#47E647] px-2 py-1 rounded-full font-[600]">
                                <p className="text-md font-[600] text-white">
                                  +{currentQuestion.points}{" "}
                                  {t("quizGame.points earned")}
                                </p>
                              </div>
                            )}
                          </div>
                          <p className="text-[18px] mt-1 text-white">
                            {currentAnswer.feedback}
                          </p>

                          <div className="bg-white rounded-xl p-3 my-2 text-black">
                            <button className="text-[#fff] cursor-default text-[12px] md:text-[16px] font-[600] bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] rounded-full px-2 py-1 flex items-center gap-2 w-[fit-content]">
                              <span className="bg-white rounded-full w-7 h-7 flex items-center justify-center">
                                <Bulb />
                              </span>{" "}
                              {t("quizGame.Did you know")}
                            </button>
                            <p className="mt-2 text-[#7B7B7B] text-[18px] font-[Lato]">
                              {currentAnswer.didYouKnow}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                {(isSubmitted || (currentTimeLimit > 0 && timeLeft === 0)) && (
                  <button
                    className="bg-[#64DDFF] py-[10px] text-white rounded-full mt-4 w-full font-[600]"
                    onClick={handleNextQuestion}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <ButtonLoader />
                    ) : currentStep < quizData.length - 1 ? (
                      t("quizGame.Next Question")
                    ) : (
                      t("quizGame.Finish Quiz")
                    )}
                  </button>
                )}

                {/* Show next question button only if answer is submitted or time expired */}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default QuizQuestionaire;
